import { type ZodTypeAny, z } from 'zod';
import { maestroDateRefined } from '../../utils/date';
import { assertUnreachable } from '../../utils/typescript';
import type { ProgrammingSubPlanFieldConfig } from './ProgrammingSubPlanFieldConfig';
import { UnknownValue } from './SpecificData';

export const buildSpecificDataSchema = (
  fieldConfigs: ProgrammingSubPlanFieldConfig[]
): z.ZodObject<Record<string, ZodTypeAny>> => {
  const shape: Record<string, ZodTypeAny> = {};

  for (const { field, required } of fieldConfigs) {
    const optionValues = field.options.map((o) => o.value);
    const errMsg = `Veuillez renseigner le champ « ${field.label} ».`;

    let fieldSchema: ZodTypeAny;

    switch (field.inputType) {
      case 'text':
      case 'textarea':
        fieldSchema = required
          ? z
              .string({
                error: (issue) => (issue.input == null ? errMsg : issue.message)
              })
              .min(1, errMsg)
          : z.string().nullish();
        break;

      case 'number':
        fieldSchema = required
          ? z.coerce.number({ error: errMsg }).int().nonnegative()
          : z.coerce.number().int().nonnegative().nullish();
        break;

      case 'decimal':
        fieldSchema = required
          ? z.coerce.number({ error: errMsg }).nonnegative()
          : z.coerce.number().nonnegative().nullish();
        break;

      case 'date': {
        const dateSchema = z
          .string({
            error: (issue) => (issue.input == null ? errMsg : issue.message)
          })
          .pipe(maestroDateRefined);
        fieldSchema = required ? dateSchema : dateSchema.nullish();
        break;
      }

      case 'select':
        if (optionValues.length > 0) {
          const enumSchema = z.enum(optionValues as [string, ...string[]], {
            error: () => errMsg
          });
          fieldSchema = required ? enumSchema : enumSchema.nullish();
        } else {
          fieldSchema = required
            ? z
                .string({
                  error: (issue) =>
                    issue.input == null ? errMsg : issue.message
                })
                .min(1, errMsg)
            : z.string().nullish();
        }
        break;

      case 'selectWithUnknown': {
        const allValues = [...optionValues, UnknownValue] as unknown as [
          string,
          ...string[]
        ];
        fieldSchema = z.enum(allValues, { error: () => errMsg });
        break;
      }

      case 'radio': {
        if (optionValues.length > 0) {
          const enumSchema = z.enum(optionValues as [string, ...string[]], {
            error: () => errMsg
          });
          fieldSchema = required ? enumSchema : enumSchema.nullish();
        } else {
          fieldSchema = z.string().nullish();
        }
        break;
      }

      case 'checkbox':
        fieldSchema = z.boolean().nullish();
        break;

      case 'multiselect': {
        const valueSchema =
          optionValues.length > 0
            ? z.enum(optionValues as [string, ...string[]], {
                error: () => errMsg
              })
            : z.string();
        fieldSchema = required
          ? z.array(valueSchema, { error: () => errMsg }).min(1, errMsg)
          : z.array(valueSchema).nullish();
        break;
      }

      default:
        fieldSchema = assertUnreachable(field.inputType);
    }

    shape[field.key] = fieldSchema;
  }

  return z.object(shape);
};
